import { MdInventory2 } from 'react-icons/md'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

/**
 * Exibe a composição (snapshot) de uma cobrança: itens de catálogo
 * (consultas/exames/componentes, agrupados por categoria) e materiais/insumos.
 * Reutilizado na tela de detalhe da cobrança e no formulário de cobrança.
 * Não renderiza nada quando não há composição.
 */

interface CatalogItem {
    id: number
    catalogId: number
    name?: string | null
    category?: string | null
    variationName?: string | null
    quantity?: number | null
    unitPrice?: number | null
    complimentary?: boolean | null
}

interface SupplyItem {
    id: number
    medicalSupplyId: number
    name?: string | null
    quantity?: number | null
    unitPrice?: number | null
}

interface BillingCompositionProps {
    catalogItems?: CatalogItem[] | null
    medicalSupplies?: SupplyItem[] | null
    /** Classe extra no contêiner (ex.: `lg:col-span-2`). */
    className?: string
}

/** Rótulos do agrupamento — o vocabulário varia por segmento (ver useServiceLabel). */
function buildCategoryLabels(labels: ServiceLabels): Record<string, string> {
    return {
        CONSULTATION: labels.Consultations,
        EXAM: labels.Exams,
        PROCEDURE: labels.Orders,
        PACKAGE: 'Pacotes',
        MEDICATION: 'Medicamentos',
        PRODUCT: 'Produtos',
        SERVICE: 'Serviços',
    }
}

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

export default function BillingComposition({
    catalogItems,
    medicalSupplies,
    className = '',
}: BillingCompositionProps) {
    const categoryLabels = buildCategoryLabels(useServiceLabel())
    const catItems = catalogItems || []
    const supplies = medicalSupplies || []
    if (!catItems.length && !supplies.length) return null

    const grouped = catItems.reduce(
        (acc: Record<string, CatalogItem[]>, it) => {
            const key = it.category || 'OUTROS'
            ;(acc[key] ||= []).push(it)
            return acc
        },
        {}
    )

    const lineTotal = (qty?: number | null, unit?: number | null) =>
        (unit || 0) * (qty || 1)

    return (
        <div
            className={`bg-base-100 rounded-xl border border-base-300/50 p-6 ${className}`}
        >
            <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                <MdInventory2 className="text-xl" />
                Composição da cobrança
            </h2>

            <div className="space-y-5">
                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                            {categoryLabels[cat] || cat}
                        </p>
                        <ul className="divide-y divide-base-300/50">
                            {items.map(it => (
                                <li
                                    key={it.id}
                                    className="flex items-center justify-between gap-3 py-2"
                                >
                                    <span className="min-w-0 text-sm text-base-content">
                                        <span className="text-base-content/60">
                                            {it.quantity || 1}x{' '}
                                        </span>
                                        {it.name || `Item #${it.catalogId}`}
                                        {it.variationName && (
                                            <span className="text-base-content/60">
                                                {' '}
                                                · {it.variationName}
                                            </span>
                                        )}
                                        {it.complimentary && (
                                            <span className="badge badge-ghost badge-sm ml-2">
                                                Cortesia
                                            </span>
                                        )}
                                    </span>
                                    <span className="shrink-0 text-sm font-medium">
                                        {it.complimentary
                                            ? '—'
                                            : formatCurrency(
                                                  lineTotal(
                                                      it.quantity,
                                                      it.unitPrice
                                                  )
                                              )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}

                {supplies.length > 0 && (
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                            Materiais
                        </p>
                        <ul className="divide-y divide-base-300/50">
                            {supplies.map(s => (
                                <li
                                    key={s.id}
                                    className="flex items-center justify-between gap-3 py-2"
                                >
                                    <span className="min-w-0 text-sm text-base-content">
                                        <span className="text-base-content/60">
                                            {s.quantity || 1}x{' '}
                                        </span>
                                        {s.name ||
                                            `Material #${s.medicalSupplyId}`}
                                    </span>
                                    <span className="shrink-0 text-sm font-medium">
                                        {formatCurrency(
                                            lineTotal(s.quantity, s.unitPrice)
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    )
}
